// ClientBudget stores scope twice: the singular FKs (`location`, `vertical`)
// and the M2M arrays (`location_ids`, `vertical_ids`). Rows written outside the
// serializer can carry only the FK, and the API sends the FK back only as
// `location_data` / `vertical_data` (the plain FK field is write-only). An
// empty array is truthy, so `location_ids || fallback` never reaches the
// fallback and those rows read as "All". Read scope through these helpers.
//
// A cleared picker sets `location_ids: []` and `location: ''` together; the
// empty string is deliberately not nullish, so a cleared row never falls back
// to the stale `location_data` it still carries.

const isBlank = (value) => value === null || value === undefined || value === '';

// Territory ids the segment covers, as numbers: the non-empty M2M, else the FK.
export function segmentLocationIds(segment) {
  const ids = (Array.isArray(segment?.location_ids) ? segment.location_ids : [])
    .filter((id) => !isBlank(id))
    .map(Number);
  if (ids.length) return ids;
  const single = segment?.location ?? segment?.location_data?.id;
  return isBlank(single) ? [] : [Number(single)];
}

// The segment's vertical id, or '' for "All": the FK, else the first M2M id.
export function segmentVerticalId(segment) {
  const vertical = segment?.vertical
    ?? segment?.vertical_data?.id
    ?? (Array.isArray(segment?.vertical_ids) ? segment.vertical_ids[0] : undefined);
  return isBlank(vertical) ? '' : vertical;
}

// Sorted, comma-joined territory ids — for dirty checks and duplicate keys,
// where [A,B] and [B,A] are the same segment.
export function segmentLocationKey(segment) {
  return segmentLocationIds(segment).sort((a, b) => a - b).join(',');
}

// Funded segments that newly mix scopes within a vertical: a market budget
// beside an "All territories" budget (or the reverse). One account runs on
// either one all-market budget or a budget per market; a few market rows on
// top of the all row split spend wrong. A vertical of "All" overlaps every
// vertical. Only segments whose scope isn't already funded in `baseline`
// count, so an account that has always mixed isn't nagged on every submit.
export function findScopeMixSegments(segments, baseline = []) {
  const isFunded = (s) => !s?.is_turned_off && !s?.has_pending_turn_off && Number(s?.amount) > 0;
  const scopeKey = (s) => `${segmentVerticalId(s)}|${segmentLocationKey(s)}`;
  const isMarket = (s) => segmentLocationIds(s).length > 0;
  const overlaps = (a, b) => {
    const va = String(segmentVerticalId(a));
    const vb = String(segmentVerticalId(b));
    return va === '' || vb === '' || va === vb;
  };
  const baselineKeys = new Set((baseline || []).filter(isFunded).map(scopeKey));
  const funded = (segments || []).filter(isFunded);
  return funded.filter((segment) => !baselineKeys.has(scopeKey(segment))
    && funded.some((other) => other !== segment
      && isMarket(other) !== isMarket(segment)
      && overlaps(segment, other)));
}
